import React from 'react'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    CONFIDENTIALITY_LEVELS,
    INTEGRITY_LEVELS,
    AVAILABILITY_LEVELS,
} from '@/modules/Asset/constants'
import { AssetFormData } from '..'

interface SecurityClassificationSectionProps {
    formData: AssetFormData
    setFormData: React.Dispatch<React.SetStateAction<AssetFormData>>
}

const SecurityClassificationSection: React.FC<
    SecurityClassificationSectionProps
> = ({ formData, setFormData }) => {
    const { t } = useTranslation('asset')

    return (
        <div
            className="rounded-lg border border-gray-100 p-4 space-y-4"
            data-tour="security-info-form"
        >
            <div className="flex flex-col gap-1">
                <h4 className="text-sm font-medium text-gray-800">
                    {t('form.securityClassification')}
                </h4>
                <p className="text-xs text-muted-foreground">
                    <a
                        href="https://drive.google.com/file/d/1t8NSSkskLXbeSMNIayj9mCbsCCCgFu0I/view?usp=sharing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-blue-500 hover:text-blue-700"
                    >
                        {t('form.securityClassificationDescription')}
                    </a>
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <Label htmlFor="confidentialityLevel">
                        {t('labels.confidentialityLevel')}{' '}
                        <span className="text-destructive">*</span>
                    </Label>
                    <Select
                        value={formData.confidentialityLevel}
                        onValueChange={(value) =>
                            setFormData((prev) => ({
                                ...prev,
                                confidentialityLevel: value,
                            }))
                        }
                    >
                        <SelectTrigger>
                            <SelectValue
                                placeholder={t('placeholders.selectLevel')}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {CONFIDENTIALITY_LEVELS.filter(
                                (level) => level.value !== 'PENDING'
                            ).map((level) => (
                                <SelectItem
                                    key={level.value}
                                    value={level.value}
                                >
                                    {t(`confidentiality_levels.${level.value}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label htmlFor="integrityLevel">
                        {t('labels.integrityLevel')}{' '}
                        <span className="text-destructive">*</span>
                    </Label>
                    <Select
                        value={formData.integrityLevel}
                        onValueChange={(value) =>
                            setFormData((prev) => ({
                                ...prev,
                                integrityLevel: value,
                            }))
                        }
                    >
                        <SelectTrigger>
                            <SelectValue
                                placeholder={t('placeholders.selectLevel')}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {INTEGRITY_LEVELS.filter(
                                (level) => level.value !== 'PENDING'
                            ).map((level) => (
                                <SelectItem
                                    key={level.value}
                                    value={level.value}
                                >
                                    {t(`integrity_levels.${level.value}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label htmlFor="availabilityLevel">
                        {t('labels.availabilityLevel')}{' '}
                        <span className="text-destructive">*</span>
                    </Label>
                    <Select
                        value={formData.availabilityLevel}
                        onValueChange={(value) =>
                            setFormData((prev) => ({
                                ...prev,
                                availabilityLevel: value,
                            }))
                        }
                    >
                        <SelectTrigger>
                            <SelectValue
                                placeholder={t('placeholders.selectLevel')}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {AVAILABILITY_LEVELS.filter(
                                (level) => level.value !== 'PENDING'
                            ).map((level) => (
                                <SelectItem
                                    key={level.value}
                                    value={level.value}
                                >
                                    {t(`availability_levels.${level.value}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </div>
    )
}

export default SecurityClassificationSection
